import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Globe,
  Plus,
  Trash2,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Shield,
  ExternalLink,
  Copy,
  Check,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { ConfirmModal } from '@/components/ui/Modal';
import { useToast } from '@/contexts/ToastContext';
import type { Project } from '@/types';

interface CustomDomain {
  id: string;
  domain: string;
  status: 'pending' | 'verified' | 'failed';
  sslStatus: 'pending' | 'active' | 'failed';
  verificationRecord?: {
    type: string;
    name: string;
    value: string;
  };
  createdAt: string;
}

// Mock domains for demo
const mockDomains: CustomDomain[] = [
  {
    id: '1',
    domain: 'myapp.example.com',
    status: 'verified',
    sslStatus: 'active',
    createdAt: '2024-01-15T10:00:00Z',
  },
  {
    id: '2',
    domain: 'staging.myapp.com',
    status: 'pending',
    sslStatus: 'pending',
    verificationRecord: {
      type: 'CNAME',
      name: '_spark-verify.staging.myapp.com',
      value: 'verify.spark.app',
    },
    createdAt: '2024-01-16T14:30:00Z',
  },
];

export function ProjectDomainsPage() {
  const { project } = useOutletContext<{ project: Project }>();
  const toast = useToast();

  const [domains, setDomains] = useState<CustomDomain[]>(mockDomains);
  const [showAddModal, setShowAddModal] = useState(false);
  const [domainToDelete, setDomainToDelete] = useState<CustomDomain | null>(null);
  const [newDomain, setNewDomain] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const handleAddDomain = async () => {
    if (!newDomain.trim()) return;

    setIsAdding(true);
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));

    const domain: CustomDomain = {
      id: Date.now().toString(),
      domain: newDomain.trim(),
      status: 'pending',
      sslStatus: 'pending',
      verificationRecord: {
        type: 'CNAME',
        name: `_spark-verify.${newDomain.trim()}`,
        value: 'verify.spark.app',
      },
      createdAt: new Date().toISOString(),
    };

    setDomains([...domains, domain]);
    setShowAddModal(false);
    setNewDomain('');
    setIsAdding(false);
    toast.success('Domain Added', 'Please configure DNS to verify your domain.');
  };

  const handleDeleteDomain = async () => {
    if (!domainToDelete) return;

    setDomains(domains.filter((d) => d.id !== domainToDelete.id));
    setDomainToDelete(null);
    toast.success('Domain Removed', 'The custom domain has been removed.');
  };

  const handleVerify = async (domainId: string) => {
    toast.info('Verifying', 'Checking DNS records...');
    // Simulate verification
    await new Promise((resolve) => setTimeout(resolve, 2000));

    setDomains(
      domains.map((d) =>
        d.id === domainId
          ? { ...d, status: 'verified', sslStatus: 'active' }
          : d
      )
    );
    toast.success('Verified', 'Domain has been verified and SSL is active.');
  };

  return (
    <div className="h-full p-6 max-w-4xl mx-auto space-y-6 overflow-y-auto">
      {/* Default Domain */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Default Domain
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-dark-900 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                <Globe className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  {project.slug}.spark.app
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="success" size="sm">
                    <Shield className="w-3 h-3 mr-1" />
                    SSL Active
                  </Badge>
                  <Badge variant="info" size="sm">Default</Badge>
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(`https://${project.slug}.spark.app`, '_blank')}
              rightIcon={<ExternalLink className="w-4 h-4" />}
            >
              Open
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Custom Domains */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Custom Domains</CardTitle>
          <Button
            size="sm"
            onClick={() => setShowAddModal(true)}
            leftIcon={<Plus className="w-4 h-4" />}
          >
            Add Domain
          </Button>
        </CardHeader>
        <CardContent>
          {domains.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Globe className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No custom domains configured</p>
              <p className="text-sm mt-1">
                Add a custom domain to use your own URL
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {domains.map((domain) => (
                <DomainCard
                  key={domain.id}
                  domain={domain}
                  onVerify={() => handleVerify(domain.id)}
                  onDelete={() => setDomainToDelete(domain)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Domain Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setNewDomain('');
        }}
        title="Add Custom Domain"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddDomain}
              isLoading={isAdding}
              disabled={!newDomain.trim()}
            >
              Add Domain
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label="Domain Name"
            placeholder="myapp.example.com"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            hint="Enter the domain you want to use for this project"
          />
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
              After adding your domain:
            </h4>
            <ol className="list-decimal list-inside text-sm text-blue-700 dark:text-blue-300 space-y-1">
              <li>Add the verification CNAME record to your DNS</li>
              <li>Click "Verify" to check DNS configuration</li>
              <li>SSL certificate will be automatically provisioned</li>
            </ol>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={!!domainToDelete}
        onClose={() => setDomainToDelete(null)}
        onConfirm={handleDeleteDomain}
        title="Remove Domain"
        message={`Are you sure you want to remove "${domainToDelete?.domain}"? This will make your project inaccessible via this domain.`}
        confirmText="Remove"
        variant="danger"
      />
    </div>
  );
}

function DomainCard({
  domain,
  onVerify,
  onDelete,
}: {
  domain: CustomDomain;
  onVerify: () => void;
  onDelete: () => void;
}) {
  const [copied, setCopied] = useState<string | null>(null);

  const handleCopy = async (text: string, field: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="p-4 border border-gray-200 dark:border-dark-700 rounded-lg">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              domain.status === 'verified'
                ? 'bg-green-100 dark:bg-green-900/30'
                : domain.status === 'failed'
                ? 'bg-red-100 dark:bg-red-900/30'
                : 'bg-yellow-100 dark:bg-yellow-900/30'
            }`}
          >
            {domain.status === 'verified' ? (
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            ) : domain.status === 'failed' ? (
              <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
            ) : (
              <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
            )}
          </div>
          <div>
            <p className="font-medium text-gray-900 dark:text-white">{domain.domain}</p>
            <div className="flex items-center gap-2 mt-1">
              <DomainStatusBadge status={domain.status} />
              <SslStatusBadge status={domain.sslStatus} />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {domain.status === 'pending' && (
            <Button variant="outline" size="sm" onClick={onVerify}>
              <RefreshCw className="w-4 h-4 mr-1" />
              Verify
            </Button>
          )}
          {domain.status === 'verified' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(`https://${domain.domain}`, '_blank')}
            >
              <ExternalLink className="w-4 h-4" />
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onDelete}>
            <Trash2 className="w-4 h-4 text-red-500" />
          </Button>
        </div>
      </div>

      {/* Verification Instructions */}
      {domain.status === 'pending' && domain.verificationRecord && (
        <div className="p-3 bg-gray-50 dark:bg-dark-900 rounded-lg">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Add this DNS record to verify your domain:
          </p>
          <div className="grid grid-cols-3 gap-2 text-sm">
            <div>
              <p className="text-gray-500 dark:text-gray-400">Type</p>
              <p className="font-mono">{domain.verificationRecord.type}</p>
            </div>
            <div>
              <p className="text-gray-500 dark:text-gray-400">Name</p>
              <div className="flex items-center gap-1">
                <p className="font-mono truncate">{domain.verificationRecord.name}</p>
                <button
                  onClick={() => handleCopy(domain.verificationRecord!.name, 'name')}
                  className="text-gray-400 hover:text-gray-600"
                >
                  {copied === 'name' ? (
                    <Check className="w-3 h-3" />
                  ) : (
                    <Copy className="w-3 h-3" />
                  )}
                </button>
              </div>
            </div>
            <div>
              <p className="text-gray-500 dark:text-gray-400">Value</p>
              <div className="flex items-center gap-1">
                <p className="font-mono">{domain.verificationRecord.value}</p>
                <button
                  onClick={() => handleCopy(domain.verificationRecord!.value, 'value')}
                  className="text-gray-400 hover:text-gray-600"
                >
                  {copied === 'value' ? (
                    <Check className="w-3 h-3" />
                  ) : (
                    <Copy className="w-3 h-3" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DomainStatusBadge({ status }: { status: string }) {
  if (status === 'verified') {
    return <Badge variant="success" size="sm">Verified</Badge>;
  }
  if (status === 'failed') {
    return <Badge variant="danger" size="sm">Failed</Badge>;
  }
  return <Badge variant="warning" size="sm">Pending Verification</Badge>;
}

function SslStatusBadge({ status }: { status: string }) {
  if (status === 'active') {
    return (
      <Badge variant="success" size="sm">
        <Shield className="w-3 h-3 mr-1" />
        SSL Active
      </Badge>
    );
  }
  if (status === 'failed') {
    return <Badge variant="danger" size="sm">SSL Failed</Badge>;
  }
  return <Badge variant="default" size="sm">SSL Pending</Badge>;
}
